import { Prediction } from 'components/commercetools-ui/atoms/auto-complete-google-places/types';
import { COUNTRY_ABBREVIATION, PUERTO_RICO } from './constant';

type LocationInfo = {
  lat?: number;
  lng?: number;
  city?: string;
  zip?: string;
  state?: string;
  countryFullName?: string;
};

export function extractCityStateFromZipPrediction(prediction: Prediction) {
  const secondary = prediction.structured_formatting.secondary_text ?? ''; // "New York, NY, USA"
  const parts = secondary.split(',');

  const city = parts[0]?.trim();
  const state = parts[1]?.trim();

  if (city && state) {
    return { state, location: `${city}, ${state}` }; // "New York, NY"
  }

  return { state, location: prediction.description }; // fallback
}

function isZipSearch(prediction: Prediction) {
  return /^\d{5}$/.test(prediction.structured_formatting.main_text);
}

export function formatPredictionLabel(prediction: Prediction, stateAbbreviation: Record<string, string>) {
  const isZip = isZipSearch(prediction);

  if (isZip) {
    return extractCityStateFromZipPrediction(prediction); // e.g., "New York, NY"
  } else {
    const city = prediction.structured_formatting.main_text?.split(',')?.[0]?.trim();
    const secondary = prediction.structured_formatting.secondary_text;
    const secondaryParts = secondary?.split(',')?.map((s) => s?.trim()) ?? [];
    const isPuertoRico = secondaryParts.includes(PUERTO_RICO) || city?.trim() === PUERTO_RICO;
    let state: string = '';

    if (isPuertoRico) {
      state = stateAbbreviation[PUERTO_RICO];
    } else if (secondary === COUNTRY_ABBREVIATION) {
      state = stateAbbreviation[city];
    } else {
      state =
        secondary
          ?.split(',')?.[0]
          ?.trim()
          ?.replace(/[^a-zA-Z]+/g, '') ?? ''; // NY
    }
    const loc = [city, state].filter((x) => x).join(', ');
    return { state, location: loc };
  }
}

export async function getPlaceDetails(placeId: string) {
  const place = new google.maps.places.Place({
    id: placeId,
    requestedLanguage: 'en',
  });

  try {
    await place.fetchFields({
      fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'],
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to fetch place details for placeId "${placeId}": ${message}`);
  }

  const lat = place.location?.lat();
  const lng = place.location?.lng();

  const zip = place.addressComponents?.find((c: any) => c.types?.includes('postal_code'))?.longText;

  const countryName = place.addressComponents?.find((c: any) => c.types?.includes('country'))?.longText || "";

  return { lat, lng, zip, countryName };
}
export function getCityFromCoords(lat: number, lng: number): Promise<google.maps.GeocoderResult[] | null> {
  return new Promise((resolve, reject) => {
    const geocoder = new google.maps.Geocoder();
    const latlng = { lat, lng };

    geocoder.geocode({ location: latlng }, (results, status) => {
      if (status === 'OK' && results) {
        resolve(results);
      } else {
        console.error('Geocoder failed due to:', status);
        reject(status);
      }
    });
  });
}

export async function getCurrentLocationData(): Promise<LocationInfo> {
  // Step 1: Get coordinates using Geolocation API
  const position: GeolocationPosition = await new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    });
  });

  const lat = position.coords.latitude;
  const lng = position.coords.longitude;

  // Step 2: Reverse geocode using Google Maps API
  const results = await getCityFromCoords(lat, lng);

  const addressComponents = results?.at(0)?.address_components ?? [];

  // Step 3: Extract city, state and zip code
  const cityComponent = addressComponents.find(
    (comp) => comp.types.includes('locality') || comp.types.includes('sublocality'),
  );

  const zipComponent = addressComponents.find((comp) => comp.types.includes('postal_code'));
  const state = addressComponents.find((c) => c.types.includes('administrative_area_level_1'))?.short_name;
  const countryFullName = addressComponents.find((c) => c.types.includes('country'))?.long_name;
  return {
    lat,
    lng,
    city: cityComponent?.long_name,
    zip: zipComponent?.long_name,
    state,
    countryFullName: countryFullName,
  };
}

export async function getFinalLocation(
  place: Prediction,
  stateValue: Record<string, string>,
): Promise<{
  lat: number | undefined;
  lng: number | undefined;
  location: string;
  state?: string;
  countryFullName?: string;
}> {
  const { lat, lng, countryName } = await getPlaceDetails(place.place_id);
  const { location, state } = formatPredictionLabel(place, stateValue);

  return { lat, lng, location, state, countryFullName: countryName };
}
